'use client'

import clsx from 'clsx'
import { formatDate } from 'date-fns'
import { NaiveDate } from 'lib/utilities/dateTime'
import { isRoundTrip } from 'lib/utilities/order'
import { useEffect, useState } from 'react'
import Header from '../Header/Header'
import Icon from '../Icon/Icon'

export interface ShipmentOrderSummaryProps {
  /**
   * Optional eyebrow. If not provided,
   * will automatically determined by the component if 'one way' or 'round trip'
   */
  eyebrow?: string
  /**
   * Optional class to able to append
   */
  className?: string
  /**
   * The oder legs data
   */
  legs: OrderLeg[]
}

interface ShipmentOrderSummaryItemProps {
  /**
   * Date to be formatted
   * accepts string
   */
  date: string
  /**
   * City to be concatenate with State
   */
  city: string
  /**
   * State to be concatenate with City
   */
  state: string
}

function ShipmentOrderSummaryItem({
  date,
  city,
  state,
}: ShipmentOrderSummaryItemProps) {
  return (
    <div className="w-full max-w-[175px]">
      <strong aria-label="ShipmentDate" className="type-body-7">
        {date && formatDate(new NaiveDate(date), 'EEE, MMM. dd')}
      </strong>
      <div aria-label="ShipmentCity" className="type-body-8">
        {city}, {state}
      </div>
    </div>
  )
}

export default function ShipmentOrderSummary({
  eyebrow,
  className,
  legs = [],
}: ShipmentOrderSummaryProps) {
  const [currentEyebrow, setEyebrow] = useState('')
  const [legItems, setLegItems] = useState<OrderLeg[]>([])

  useEffect(() => {
    if (legs?.length) {
      setEyebrow(
        eyebrow ? eyebrow : isRoundTrip(legs) ? 'ROUND TRIP' : 'ONE WAY',
      )
      setLegItems(legs)
    }
  }, [legs, eyebrow])

  return (
    <div className={clsx('bg-white p-4 rounded-lg', className)}>
      <Header
        as="h4"
        aria-label="TripType"
        className="type-eyebrow-2 mb-4"
        overrideClassName
      >
        {currentEyebrow}
      </Header>
      <div className="flex flex-col gap-4">
        {legItems[0]?.travelers?.length &&
        legItems[0]?.origin?.city &&
        legItems[0]?.destination?.city
          ? legs.map((legItem, legKey) => (
              <div
                aria-label="ShipLeg"
                className="flex items-center"
                key={legKey}
              >
                <ShipmentOrderSummaryItem
                  date={legItem.travelers?.[0]?.shipments?.[0]?.ship_date}
                  city={legItem.origin.city}
                  state={legItem.origin.state}
                />
                <Icon
                  icon="arrow-right"
                  className="w-6 h-6 text-theme-accessibility mx-4"
                />
                <ShipmentOrderSummaryItem
                  date={legItem.travelers?.[0]?.shipments?.[0]?.arrival_date}
                  city={legItem.destination.city}
                  state={legItem.destination.state}
                />
              </div>
            ))
          : '—'}
      </div>
    </div>
  )
}
